'use strict';

angular.module('ecrd.states')
    .provider('RecordStatesService', function() {
        var provider = this;

        var _states = {};
        var _lock = null;

        provider.setStates = function(states) {
            _states = states;
        };

        provider.setLock = function(lockInfo) {
            _lock = lockInfo;
        };

        this.$get = RecordStatesService;

        RecordStatesService.$inject = [
            '$log',
            '$q',
            'AccountManager',
            'Event',
            'Modal',
            'RecordDataService',
            'RecordMetadataService',
            'RecordsManagerService',
            'RecordValidationService',
            'ShareCrfSentry',
        ];

        function RecordStatesService(
            $log,
            $q,
            AccountManager,
            Event,
            Modal,
            RecordDataService,
            RecordMetadataService,
            RecordsManagerService,
            RecordValidationService,
            ShareCrfSentry
        ) {
            function hasLock() {
                return _lock !== null;
            }

            function getAvailableStates() {
                return _states;
            }

            function isAvailable() {
                return Object.keys(getAvailableStates()).length > 0 || hasLock();
            }

            function isLocked() {
                return hasLock() && RecordMetadataService.isRecordLocked();
            }

            function _reloadMetadata(record) {
                RecordMetadataService.load(record.metadata);

                return $q.resolve(true);
            }

            function _updateState(stateId) {
                var stateValue = RecordMetadataService.hasRecordState(stateId);
                var newValue = !stateValue;
                var currentValue = stateValue; // Define currentValue based on stateValue

                var stateInfo = getAvailableStates()[stateId];

                var needsSignature = newValue && stateInfo.sign;
                var promise = $q.resolve(true);

                var updateStateSpan = null;
                var updateStateSpanData = {
                    name: 'Update Record State',
                    tags: {
                        'sharecrf.state.id': stateId,
                        'sharecrf.state.name': stateInfo.name,
                        'sharecrf.state.current_state': currentValue,
                        'sharecrf.state.target_state': newValue,
                    },
                    asParent: true,
                };

                if (needsSignature) {
                    // Confirmation modal
                    promise = Modal.open({
                        templateUrl: 'html/entity/modal-signature-needed.html',
                        controller: 'EntityModalSignatureNeededController',
                        controllerAs: 'modal',
                        onlyActions: true,
                        data: {
                            message: stateInfo.signMessage,
                        },
                    }).closePromise; // Modal result, 'Confirm' button pressed
                }

                var params = {
                    context: 'record',
                    state: stateId,
                };

                return promise
                    .then(function(result) {
                        updateStateSpan = ShareCrfSentry.startTransaction(updateStateSpanData);

                        $log.debug('Set record state ' + stateId + ' to', newValue);

                        params.enabled = newValue;
                        params.calculateQueries = true;

                        // If signature is required, password comes from modal controller
                        if (needsSignature) {
                            params.password = result;
                        }

                        // Update record state value
                        return RecordsManagerService.setGlobalState(params);
                    })
                    .catch(function(error) {
                        // Queries que se van a abrir al hacer el update
                        if (error.code === 'queries-to-create') {
                            $log.debug('Queries to create', error.info.queriesToCreate);
                            var queryCandidates = error.info.queriesToCreate;

                            var queries = queryCandidates.map(function(query) {
                                var fieldPath = RecordDataService.getElementFullLabeledPathByUID(query.fieldUId);

                                return {
                                    label: fieldPath.join(' > '),
                                    text: query.text,
                                };
                            });

                            return Modal.open({
                                template: 'modules/ecrd/components/rules/queries/content.html',
                                controller: 'RulesActionCreateQueriesController',
                                controllerAs: '$ctrl',
                                data: {
                                    queries: queries,
                                },
                                onlyActions: true,
                            })
                                .closePromise
                                .then(function() {
                                    // Volvemos a reintentar el update, quitando la comprobación de queries
                                    delete params.calculateQueries;

                                    updateStateSpan = ShareCrfSentry.startTransaction(updateStateSpan);


                                    return RecordsManagerService.setGlobalState(params);
                                });
                        }

                        // Error cuando hay cambios pendientes que desactivarían el estado
                        if (error.code === 'approval-state-pending-changes') {
                            $log.debug('Approval status pending changes detected', error.info);
                            // El error se propaga al controlador para que muestre el mensaje apropiado
                            // No rompemos el flujo, simplemente rechazamos la promesa con el error
                            throw error;
                        }

                        if (error !== Modal.DISMISSED) {
                            $log.error(error, 'modal queries to create on form completion');
                        }

                        throw error;
                    })
                    .then(_reloadMetadata)
                    .then(function() {
                        ShareCrfSentry.setTransaction('toggle-record-state', updateStateSpan);
                        Event.broadcast('entityActionEnd', ['state', stateId, newValue]);
                    });
            }

            function _updateLock() {
                var currentValue = isLocked();
                var newValue = !currentValue; // Valor a establecer
                var needsSignature = !!_lock.sign && newValue;


                var promise = $q.resolve(true);

                var updateLockSpan = null;
                var updateLockSpanData = {
                    name: 'Update Record Lock',
                    op: 'http.client',
                    tags: {
                        'sharecrf.lock.current_state': currentValue,
                        'sharecrf.lock.target_state': newValue,
                    },
                    asParent: true,
                };

                if (needsSignature) {
                    // Confirmation modal
                    promise = Modal.open({
                        templateUrl: 'generated/html/entity-lock.modal.html',
                        controller: 'LockEntityModalController',
                        controllerAs: 'modal',
                    }).closePromise; // Modal result, 'Confirm' button pressed
                }

                return promise
                    .then(function(result) {
                        updateLockSpan = ShareCrfSentry.startTransaction(updateLockSpanData);
                        $log.debug('Lock record to', newValue);

                        var params = {
                            context: 'record',
                            state: 'locked',
                            enabled: newValue,
                        };

                        // If signature is required, password comes from modal controller
                        if (needsSignature) {
                            params.password = result.password;
                        }

                        return RecordsManagerService.setGlobalLock(params);
                    })
                    .then(_reloadMetadata)
                    .then(function() {
                        ShareCrfSentry.setTransaction('toggle-record-lock', updateLockSpan);
                        Event.broadcast('entityActionEnd', ['state', 'lock', newValue]);
                    });
            }

            // Obtiene solamente la información de los estados habilitados
            function getEnabledStates() {
                var allStates = getAvailableStates();

                var enabledStates = {};
                angular.forEach(allStates, function(stateInfo, stateId) {
                    if (RecordMetadataService.hasRecordState(stateId)) {
                        enabledStates[stateId] = stateInfo;
                    }
                });

                return enabledStates;
            }

            function getDisabledStates() {
                var allStates = getAvailableStates();

                var disabledStates = {};
                angular.forEach(allStates, function(stateInfo, stateId) {
                    var stateMetadata = RecordMetadataService.getGlobalState(stateId);
                    if (angular.isObject(stateMetadata) && stateMetadata.value === false) {
                        disabledStates[stateId] = stateInfo;
                    }
                });

                return disabledStates;
            }

            /**
             * Obtiene información sobre el estado de edición de cada estado
             *
             * @return {object} Objeto indexado por ID de estado, especificando si es editable o no, y la razón en caso
             *                  de no serlo
             */
            function getEditionInfo() {
                var editionInfo = {};

                var isRecordValid = RecordValidationService.isRecordValid();
                var recordLocked = isLocked();

                // Motivo asociado
                var reason = isRecordValid ? null : 'validation';

                var permission;

                if (hasLock()) {
                    var lockReason = reason;
                    permission = recordLocked ? 'unlock records' : 'lock records';
                    if (!AccountManager.hasPermission(permission)) {
                        lockReason = 'permission';
                    }

                    editionInfo.lock = {
                        editable: !lockReason,
                        reason: lockReason,
                    };
                }

                // Y para el resto de estados se requiere, además, que el registro no esté bloqueado
                if (!reason && recordLocked) {
                    reason = 'lock';
                }

                var enabledStates = getEnabledStates();

                angular.forEach(getAvailableStates(), function(stateInfo, stateId) {
                    var stateReason = reason;
                    var hasState = angular.isDefined(enabledStates[stateId]);
                    permission = hasState ? 'unset records state ' + stateId : 'set records state ' + stateId;
                    if (!AccountManager.hasPermission(permission)) {
                        stateReason = 'permission';
                    }

                    editionInfo[stateId] = {
                        editable: !stateReason,
                        reason: stateReason,
                    };
                });

                return editionInfo;
            }

            /**
             * Has any editable state
             *
             * @return {Boolean} TRUE if user can edit states
             */
            function hasAnyEditableState() {
                var hasAny = false;

                var availableStates = getAvailableStates();
                for (var stateId in availableStates) {
                    hasAny = hasAny || AccountManager.hasAtLeastOnePermission([
                        'set records state ' + stateId,
                        'unset records state ' + stateId,
                    ]);
                }

                return hasAny;
            }

            /**
             * Has editable lock
             *
             * @return {Boolean} TRUE if user can edit lock
             */
            function hasEditableLock() {
                return hasLock() && AccountManager.hasAtLeastOnePermission(['lock records', 'unlock records']);
            }

            function getAuditChanges(stateId) {
                return RecordsManagerService.getAuditChanges({
                    stateId: stateId,
                });
            }

            return {
                isAvailable: isAvailable,
                hasAnyEditableAction: function() {
                    return hasEditableLock() || hasAnyEditableState();
                },
                getAvailableStates: getAvailableStates,
                getEditionInfo: getEditionInfo,
                getEnabledStates: getEnabledStates,
                getDisabledStates: getDisabledStates,
                getEditableStates: function() {
                    var allStates = getAvailableStates();
                    var editableStates = {};

                    angular.forEach(allStates, function(stateInfo, stateId) {
                        if (AccountManager.hasAtLeastOnePermission([
                            'set records state ' + stateId,
                            'unset records state ' + stateId,
                        ])) {
                            editableStates[stateId] = stateInfo;
                        }
                    });

                    return editableStates;
                },
                toggleState: function(stateId) {
                    var stateInfo = getAvailableStates()[stateId];
                    if (!stateInfo) {
                        return $q.reject('global-state-' + stateId + '-not-available');
                    }

                    return Event.broadcast('entityActionStart', ['state', stateId])
                        .then(function(event) {
                            return event.defaultPrevented ? 'cancel' : _updateState(stateId);
                        });
                },
                hasLock: hasLock,
                toggleLock: function() {
                    if (!hasLock()) {
                        return $q.reject('global-lock-not-available');
                    }

                    return Event.broadcast('entityActionStart', ['state', 'lock'])
                        .then(function(event) {
                            return event.defaultPrevented ? 'cancel' : _updateLock();
                        });
                },
                isLocked: isLocked,
                hasEditableLock: hasEditableLock,
                isRecordInState: function(stateId) {
                    return RecordMetadataService.hasRecordState(stateId);
                },
                getAuditChanges: getAuditChanges,
            };
        }
    });
